import { createSelector } from "reselect";
import type { RootState } from "~/app/store";
import { type WishlistEntryModel, type WishlistModel } from "~/features/wishlist/types/model";
import {
  selectCachedWishlists,
  selectCachedWishlistsMap,
  selectCachedEntriesMap,
  selectIsWishlistsError as selectIsWishlistsErrorFromSlice,
  selectIsWishlistsLoading as selectIsWishlistsLoadingFromSlice,
} from "./wishlistSlice";

// ============================================================================
// Core Selectors - Data from RTK Query cache
// ============================================================================

/** Are wishlists loading (initial fetch or refetch)? */
export const selectIsWishlistsLoading = selectIsWishlistsLoadingFromSlice;

/** Did the wishlist fetch fail? */
export const selectIsWishlistsError = selectIsWishlistsErrorFromSlice;

/** Get all wishlists */
export const selectWishlists = selectCachedWishlists;

/** Get selected wishlist ID from UI state */
export const selectSelectedWishlistId = (state: RootState): string | null => state.wishlistUI.selectedWishlistId;

/** Get the selected wishlist object */
export const selectSelectedWishlist = createSelector(
  selectSelectedWishlistId,
  selectCachedWishlistsMap,
  (wishlistId, wishlistsMap): WishlistModel | null => {
    if (!wishlistId) {
      return null;
    }
    return wishlistsMap[wishlistId] ?? null;
  },
);

/** Get entries for the selected wishlist */
export const selectSelectedWishlistEntries = createSelector(
  selectSelectedWishlist,
  selectCachedEntriesMap,
  (wishlist, entriesMap): WishlistEntryModel[] => {
    if (!wishlist) {
      return [];
    }
    return wishlist.entryIds.map((id) => entriesMap[id]).filter((e): e is WishlistEntryModel => !!e);
  },
);

/** Get wishlist by ID */
export const selectWishlistById = (state: RootState, wishlistId: string): WishlistModel | undefined => {
  return selectCachedWishlistsMap(state)[wishlistId];
};

/** Get size of a specific wishlist */
export const selectWishlistSize = (wishlistId: string) => (state: RootState) => {
  return selectWishlistById(state, wishlistId)?.entryIds.length ?? 0;
};

/** Total number of wishlists */
export const selectWishlistAmount = (state: RootState) => selectCachedWishlists(state).length;

// ============================================================================
// Derived Selectors
// ============================================================================

/** Check if the selected wishlist has been processed (details fetched) */
export const selectIsSelectedWishlistProcessed = (state: RootState) => {
  const selectedId = state.wishlistUI.selectedWishlistId;
  if (!selectedId) {
    return false;
  }
  return state.wishlistUI.processedWishlistIds.includes(selectedId);
};
